import { z } from "zod";
import { customerChannelIdSchema } from "./messaging.js";
// The conversation and handoff contract of Milestone C (CHT-730..735; docs/architecture/milestone-c-contracts.md, sections
// 4.4, 4.5 and 7; ADRs 0011 and 0012). FROZEN at the Wave 0 commit. Lane C (handoff backend) implements the commands, lane E
// (staff web) calls them, lane A reads the mode and the generation.
//
// Same rules as `customer-memory.ts`: strict top-level shapes, ISO 8601 text for times, codes repeated from the domain and
// parity-tested in `@lafayette/agent-contract` (`milestone-c-parity.test.ts`).
//
// Concurrency: every staff command on a handoff carries the `expectedVersion` it read AND an `Idempotency-Key` header. A
// stale version is a 409 whose body carries `handoffConflict`; the client refreshes to the canonical owner.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
const version = z.number().int().min(1).max(2_147_483_647);
/** A staff-internal reason. Never audit metadata, never a customer view, never analytics. */
const staffReason = z.string().trim().min(1).max(500);
// --- codes (parity-tested) ----------------------------------------------------------------------------------------------
export const conversationModeValues = [
    "AI_ACTIVE",
    "HANDOFF_REQUESTED",
    "WAITING_FOR_CONCIERGE",
    "HUMAN_ACTIVE",
];
export const handoffStatusValues = [
    "REQUESTED",
    "WAITING",
    "ACCEPTED",
    "RELEASED",
    "CANCELLED",
];
export const handoffTargetKindValues = [
    "TEMPORARY",
    "PRIMARY",
    "BACKUP",
    "SUPERVISOR_QUEUE",
    "UNASSIGNED",
    "DIRECT",
];
export const handoffReasonCodeValues = [
    "CUSTOMER_REQUESTED_PERSON",
    "SUPPLIER_CONTACT_REQUIRED",
    "CONCIERGE_JUDGMENT_REQUIRED",
    "CATALOG_DATA_CONFLICT",
    "UNSUPPORTED_HIGH_IMPACT_REQUEST",
    "COMPLAINT_SERVICE_RECOVERY",
    "PAYMENT_FULFILMENT_EXCEPTION",
    "REPEATED_NO_PROGRESS",
    "POLICY_REVIEW_REQUIRED",
];
/** Why a waiting handoff moves to another target (CHT-734). A code, never free text in metrics. */
export const handoffEscalationReasonValues = [
    "PRIMARY_UNAVAILABLE",
    "NOT_ACCEPTED_IN_TIME",
    "SUPERVISOR_OVERRIDE",
];
/**
 * Why a supervisor moves a handoff to a staff member who is on a manual unavailability window (CHT-731). Optional on
 * `reassign`; only a supervisor may send one, and it is audited. Without it an unavailable target is refused with
 * `handoff.target_unavailable`. Repeated in the domain (`handoffAvailabilityOverrideCodes`) and parity-tested.
 */
export const handoffAvailabilityOverrideValues = [
    "COVERAGE_GAP",
    "CUSTOMER_URGENT",
    "SUPERVISOR_JUDGMENT",
];
// --- staff handoff commands ---------------------------------------------------------------------------------------------
/** `acceptHandoff`: first accept wins. WAITING to ACCEPTED, conversation to HUMAN_ACTIVE. */
export const staffHandoffAcceptBodySchema = z.strictObject({ expectedVersion: version });
/**
 * `reassignHandoff`: one route for both domain events. WAITING: `reassign` (new target, stays WAITING). ACCEPTED:
 * `transfer_owner` (new owner, stays HUMAN_ACTIVE). The server picks the event from the current status.
 */
export const staffHandoffReassignBodySchema = z.strictObject({
    expectedVersion: version,
    targetStaffUserId: uuid,
    reason: staffReason,
    availabilityOverride: z.enum(handoffAvailabilityOverrideValues).optional(),
});
/** `releaseToAI`: ACCEPTED to RELEASED, conversation HUMAN_ACTIVE to AI_ACTIVE. The current owner or a supervisor. */
export const staffHandoffReleaseBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
});
/** The exceptional close of a REQUESTED or WAITING handoff. Supervisor only. Conversation back to AI_ACTIVE. */
export const staffHandoffCancelBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
});
/** CHT-734: a waiting handoff goes to the next eligible coverage (backup, then supervisor queue). Primary history stays. */
export const staffHandoffEscalateBodySchema = z.strictObject({
    expectedVersion: version,
    reasonCode: z.enum(handoffEscalationReasonValues),
});
/** The success body of every staff handoff command: the canonical state after the commit. */
export const staffHandoffCommandResultSchema = z.strictObject({
    handoffId: uuid,
    conversationId: uuid,
    status: z.enum(handoffStatusValues),
    version,
    conversationMode: z.enum(conversationModeValues),
    targetKind: z.enum(handoffTargetKindValues).nullable(),
    targetStaffUserId: uuid.nullable(),
    ownerStaffUserId: uuid.nullable(),
});
/** The extra key `handoffConflict` of a 409 on a handoff command. The client refreshes and shows the canonical owner. */
export const handoffConflictCodes = [
    "handoff.version_conflict",
    "handoff.not_waiting",
    "handoff.not_accepted",
    "handoff.transition_invalid",
    "handoff.already_open",
    "handoff.same_target",
    "handoff.target_ineligible",
    "handoff.target_unavailable",
    "handoff.no_escalation_target",
    "handoff.escalation_not_needed",
    "handoff.generation_stale",
];
export const handoffConflictSchema = z.strictObject({
    code: z.enum(handoffConflictCodes),
    currentVersion: version.nullable(),
    currentStatus: z.enum(handoffStatusValues).nullable(),
});
/**
 * One snapshot of the compact summary a concierge reads before accepting (CHT-734). STAFF-ONLY: the text can hold the
 * customer's own words, so it has one read path (staff who may read the handoff) and is never in a customer view, an
 * audit row, a log or an analytics event. `facts` are codes and ids; `summaryText` is at most 2,000 characters.
 * Snapshots are append-only: a newer one (the system at the request, then a copilot or a staff member) never edits an older one.
 */
export const handoffSummaryGeneratorValues = ["SYSTEM", "COPILOT", "STAFF"];
export const staffHandoffSummarySchema = z.strictObject({
    handoffId: uuid,
    version,
    generatedBy: z.enum(handoffSummaryGeneratorValues),
    facts: z.record(z.string().max(64), z.unknown()),
    summaryText: z.string().min(1).max(2000),
    createdAt: instant,
});
// --- staff inbox --------------------------------------------------------------------------------------------------------
/**
 * The inbox filters of CHT-732. Each is a server-side query over Postgres (business truth). Stream unread counts are a
 * client-side overlay only and never change membership of a filter.
 */
export const staffInboxFilterValues = [
    "MY_CUSTOMERS",
    "WAITING_HANDOFFS",
    "HUMAN_ACTIVE",
    "NEEDS_STAFF_ACTION",
    "AWAITING_CUSTOMER",
    "RECENT",
    "AT_RISK",
    // CHT-734, add-only: the coverage exceptions of a supervisor (a waiting handoff nobody can take, a customer with no effective
    // PRIMARY, a PRIMARY who is unavailable with nobody to cover). Supervisors only: any other staff member gets 403.
    "EXCEPTIONS",
];
/** Attention badges (CHT-734). Timestamps and codes only, no message content. */
export const staffAttentionCodeValues = [
    "NEW_REQUEST",
    "WAITING",
    "CUSTOMER_UPDATED_WHILE_WAITING",
    "STALE",
    "FALLBACK_ROUTED",
    "UNASSIGNED_COVERAGE",
];
export const staffInboxQuerySchema = z.strictObject({
    filter: z.enum(staffInboxFilterValues),
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const staffInboxItemSchema = z.strictObject({
    conversationId: uuid,
    customer: z.strictObject({ id: uuid, displayName: z.string().min(1).max(120) }),
    channel: z.strictObject({ type: z.literal("messaging"), id: customerChannelIdSchema }).nullable(),
    mode: z.enum(conversationModeValues),
    handoff: z
        .strictObject({
        id: uuid,
        status: z.enum(handoffStatusValues),
        version,
        reasonCode: z.enum(handoffReasonCodeValues),
        targetKind: z.enum(handoffTargetKindValues).nullable(),
        targetStaffUserId: uuid.nullable(),
        ownerStaffUserId: uuid.nullable(),
        requestedAt: instant,
        acceptedAt: instant.nullable(),
    })
        .nullable(),
    attention: z.strictObject({
        codes: z.array(z.enum(staffAttentionCodeValues)).max(6),
        lastCustomerActivityAt: instant.nullable(),
        lastStaffActivityAt: instant.nullable(),
        lastAiActivityAt: instant.nullable(),
    }),
    openWork: z.strictObject({
        conciergeRequests: z.number().int().min(0),
        paymentRequests: z.number().int().min(0),
    }),
    /** Sort key: the server orders by attention, then by this time, newest first. */
    sortAt: instant,
});
export const staffInboxPageSchema = z.strictObject({
    items: z.array(staffInboxItemSchema).max(100),
    nextCursor: z.string().min(1).max(512).nullable(),
});
